import { useState, useEffect, useRef, useCallback } from 'react';
import io from 'socket.io-client';
import ConnectingScreen from './screens/1-ConnectingScreen';
import JoinGameScreen from './screens/2-JoinGameScreen';
import LobbyScreen from './screens/3-LobbyScreen';
import WaitingForPromptsScreen from './screens/4-WaitingForPromptsScreen';
import AnnouncingJudgeScreen from './screens/5-AnnouncingJudgeScreen';
import WaitingForDrawingsScreen from './screens/6-WaitingForDrawingsScreen';
import WaitingForJudgingScreen from './screens/7-WaitingForJudgingScreen';
import RoundResultsScreen from './screens/8-RoundResultsScreen';
import FinishedScreen from './screens/9-FinishedScreen';
import ErrorScreen from './screens/0-ErrorScreen';

// Debug environment variables
console.log('Environment variables:', {
  NODE_ENV: process.env.NODE_ENV,
  REACT_APP_SERVER_URL: process.env.REACT_APP_SERVER_URL,
  REACT_APP_PLAYER_URL: process.env.REACT_APP_PLAYER_URL
});

const socket = io(process.env.REACT_APP_SERVER_URL);

function App() {
  const [roomCode, setRoomCode] = useState('');
  const roomCodeRef = useRef('');
  const [gameState, setGameState] = useState('connecting');
  const [players, setPlayers] = useState([]);
  const [currentPrompt, setCurrentPrompt] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [votingResults, setVotingResults] = useState([]);
  const [currentRound, setCurrentRound] = useState(0);
  const [totalRounds, setTotalRounds] = useState(3);
  const [finalResults, setFinalResults] = useState(null);
  const [responseTimeLeft, setResponseTimeLeft] = useState(0);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [promptCollectionPhase, setPromptCollectionPhase] = useState(false);
  const [promptsPerPlayer, setPromptsPerPlayer] = useState(1);
  const [playerPromptProgress, setPlayerPromptProgress] = useState({});
  const [currentJudgeId, setCurrentJudgeId] = useState(null);
  const [judgeAward, setJudgeAward] = useState(null);
  const [submittedPlayers, setSubmittedPlayers] = useState([]);
  const [latestPrompt, setLatestPrompt] = useState('');
  const [votingPhase, setVotingPhase] = useState(false);
  const [votingResponses, setVotingResponses] = useState([]);
  const [gameOptions, setGameOptions] = useState({});
  const [announcingJudge, setAnnouncingJudge] = useState(false);
  const resultsTimerRef = useRef(null);


  useEffect(() => {
    // Add connection debugging
    socket.on('connect', () => {
      console.log('Socket connected:', socket.id);
    });

    socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      setGameState('connecting');
    });

    socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      setErrorMessage(`Connection failed: ${error.message}`);
      setGameState('error');
    });

    // Auto-create room when connected (but stay on connecting until video ends)
    const createDisplayRoom = () => {
      console.log('Creating display room...');
      socket.emit('create-display-room', (response) => {
        console.log('Create room response:', response);
        if (response.success) {
          setRoomCode(response.roomCode);
          roomCodeRef.current = response.roomCode;
          // Don't setGameState here - wait for video to end
          setErrorMessage('');
        } else {
          setErrorMessage('Failed to create room');
          setGameState('error');
        }
      });
    };

    // Wait for socket connection, then create room
    if (socket.connected) {
      createDisplayRoom();
    } else {
      socket.once('connect', createDisplayRoom);
    }

    socket.on('players-updated', (playersList) => {
      setPlayers(playersList);
    });

    socket.on('player-joined', (playerData) => {
      console.log('Player joined display:', playerData);
    });

    socket.on('player-left', (playerId) => {
      console.log('Player left display:', playerId);
    });

    socket.on('prompt-collection-started', (data) => {
      setGameState('playing');
      setPromptCollectionPhase(true);
      setPromptsPerPlayer(data.promptsPerPlayer);
      setPlayerPromptProgress({});
      setCurrentPrompt('');
    });

    socket.on('prompt-collected', (data) => {
      setLatestPrompt(data.prompt);
      setPlayerPromptProgress(prev => ({
        ...prev,
        [data.playerName]: data.playerPromptsCount
      }));
    });

    socket.on('response-timer', (data) => {
      setResponseTimeLeft(data.timeLeft);
    });

    socket.on('response-time-up', () => {
      setResponseTimeLeft(0);
    });

    socket.on('response-submitted', (data) => {
      console.log('Response submitted on display:', data);
      setSubmittedPlayers(prev => {
        if (prev.some(p => p.playerId === data.playerId)) return prev;
        return [...prev, data];
      });
    });

    socket.on('voting-phase-started', (data) => {
      setVotingPhase(true);
      setVotingResponses(data.responses || []);
      setResponseTimeLeft(0);
    });

    socket.on('voting-complete', (data) => {
      setVotingPhase(false);
      setVotingResponses([]);
      setVotingResults(data.results);
      setCurrentRound(data.round);
      setTotalRounds(data.totalRounds);
      setJudgeAward(data.judgeAward || null);
      if (resultsTimerRef.current) clearTimeout(resultsTimerRef.current);
      resultsTimerRef.current = setTimeout(() => {
        setVotingResults([]);
      }, 15000);
    });

    socket.on('judge-selected', (data) => {
      setCurrentJudgeId(data.judgeId);
      console.log(`Judge selected: ${data.judgeName} for round ${data.round}`);
    });

    socket.on('judge-announced', (data) => {
      setCurrentJudgeId(data.judgeId);
      setCurrentRound(data.round);
      setAnnouncingJudge(true);
    });

    socket.on('round-started', (data) => {
      setAnnouncingJudge(false);
      setCurrentRound(data.round);
      setTotalRounds(data.totalRounds);
      setSubmittedPlayers([]);
      setPromptCollectionPhase(false);
      setCurrentPrompt(data.prompt || '');
      setResponseTimeLeft(data.timeLimit || 20);
      setJudgeAward(null);
      setLatestPrompt('');
      setVotingPhase(false);
      setVotingResponses([]);
      if (data.judgeId) {
        setCurrentJudgeId(data.judgeId);
      }
    });

    socket.on('game-finished', (data) => {
      if (resultsTimerRef.current) clearTimeout(resultsTimerRef.current);
      setVotingResults([]);
      setFinalResults(data);
      setGameState('finished');
    });

    socket.on('controller-changed', (data) => {
      console.log(`Game controller changed to: ${data.newControllerName}`);
    });

    socket.on('scores-updated', (playersList) => {
      setPlayers(playersList);
    });

    socket.on('host-disconnected', () => {
      setGameState('lobby');
      setCurrentPrompt('');
      setVotingResults([]);
    });

    socket.on('game-options-updated', (options) => {
      setGameOptions(options);
    });

    socket.on('game-reset', () => {
      setGameState('lobby');
      setCurrentPrompt('');
      setVotingResults([]);
      setCurrentRound(0);
      setFinalResults(null);
      setResponseTimeLeft(0);
      setPromptCollectionPhase(false);
      setPlayerPromptProgress({});
      setCurrentJudgeId(null);
      setJudgeAward(null);
      setSubmittedPlayers([]);
      setLatestPrompt('');
      setVotingPhase(false);
      setVotingResponses([]);
      if (resultsTimerRef.current) clearTimeout(resultsTimerRef.current);
    });

    return () => {
      socket.off('connect');
      socket.off('disconnect');
      socket.off('connect_error');
      socket.off('players-updated');
      socket.off('player-joined');
      socket.off('player-left');
      socket.off('response-timer');
      socket.off('response-time-up');
      socket.off('response-submitted');
      socket.off('voting-phase-started');
      socket.off('voting-complete');
      socket.off('round-started');
      socket.off('game-finished');
      socket.off('controller-changed');
      socket.off('scores-updated');
      socket.off('host-disconnected');
      socket.off('prompt-collection-started');
      socket.off('prompt-collected');
      socket.off('judge-selected');
      socket.off('judge-announced');
      socket.off('game-options-updated');
      socket.off('game-reset');
    };
  }, []);

  const handleVideoEnd = useCallback(() => {
    if (roomCodeRef.current) {
      setGameState('lobby');
    } else {
      // Room not ready yet, poll until it is
      const check = setInterval(() => {
        if (roomCodeRef.current) {
          clearInterval(check);
          setGameState('lobby');
        }
      }, 100);
    }
  }, []);

  switch (gameState) {
    case 'connecting':
      return <ConnectingScreen onVideoEnd={handleVideoEnd} />;

    case 'error':
      return <ErrorScreen message={errorMessage} />;

    case 'lobby':
      // Show JoinGame screen when no players, Lobby when players have joined
      if (players.length === 0) {
        return <JoinGameScreen roomCode={roomCode} />;
      }
      return (
        <LobbyScreen
          roomCode={roomCode}
          players={players}
        />
      );

    case 'playing':
      if (promptCollectionPhase) {
        return (
          <WaitingForPromptsScreen
            players={players}
            playerPromptProgress={playerPromptProgress}
            promptsPerPlayer={promptsPerPlayer}
            latestPrompt={latestPrompt}
          />
        );
      }
      if (votingResults.length > 0) {
        return (
          <RoundResultsScreen
            votingResults={votingResults}
            judgeAward={judgeAward}
            players={players}
            currentJudgeId={currentJudgeId}
            currentPrompt={currentPrompt}
          />
        );
      }
      if (votingPhase) {
        return (
          <WaitingForJudgingScreen
            currentPrompt={currentPrompt}
            players={players}
            currentJudgeId={currentJudgeId}
            votingResponses={votingResponses}
          />
        );
      }
      if (announcingJudge) {
        return (
          <AnnouncingJudgeScreen
            players={players}
            currentJudgeId={currentJudgeId}
          />
        );
      }
      return (
        <WaitingForDrawingsScreen
          currentPrompt={currentPrompt}
          responseTimeLeft={responseTimeLeft}
          submittedPlayers={submittedPlayers}
          players={players}
          currentJudgeId={currentJudgeId}
          disableTimers={gameOptions.disableTimers}
        />
      );

    case 'finished':
      return (
        <FinishedScreen
          roomCode={roomCode}
          finalResults={finalResults}
        />
      );

    default:
      return null;
  }
}

export default App;